// Campaign params from the landing URL, carried to the Shopify checkout URL
// (Shopify's conversion summary reads utm_* from it) and click ids onto the
// cart for the GA4 webhook. Kept in localStorage so a return visit or a new tab
// still reaches checkout with them.

const STORAGE_KEY = 'campaignParams'
// Shopify's customer journey attributes within 30 days.
const TTL_MS = 30 * 24 * 60 * 60 * 1000

// Google Ads click identifiers, read alongside utm_* params. gclid is the
// standard click id; gbraid/wbraid are its iOS privacy-safe variants (used when
// ITP/ATT suppress gclid); gad_source marks the ad surface.
const GOOGLE_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', 'gad_source']

interface StoredCampaignParams {
  params: Record<string, string>
  savedAt: number
}

const isStoredCampaignParams = (
  value: unknown
): value is StoredCampaignParams => {
  if (typeof value !== 'object' || value === null) return false

  const { params, savedAt } = value as Partial<StoredCampaignParams>
  return (
    Number.isFinite(savedAt) &&
    typeof params === 'object' &&
    params !== null &&
    !Array.isArray(params) &&
    Object.values(params).every((v) => typeof v === 'string')
  )
}

// Runs from onClientEntry, before React renders, so it sees the landing URL
// untouched. A tagged landing replaces the stored set; an untagged one keeps it.
// Duplicate keys keep the last value: Google Shopping URLs carry Merchant
// Center's utm_* first and the Ads template's (the paid click) after.
export const captureCampaignParams = () => {
  const params: Record<string, string> = {}

  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (
      value !== '' &&
      (key.startsWith('utm_') || GOOGLE_CLICK_PARAMS.includes(key))
    ) {
      params[key] = value
    }
  }

  if (Object.keys(params).length === 0) return

  try {
    const stored: StoredCampaignParams = { params, savedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage unavailable; checkout goes out without campaign params.
  }
}

export const getCampaignParams = (): Record<string, string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}

    const stored: unknown = JSON.parse(raw)
    if (!isStoredCampaignParams(stored)) return {}

    return Date.now() - stored.savedAt < TTL_MS ? stored.params : {}
  } catch {
    return {}
  }
}
